import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function BestRoadTripFromVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-road-trip-from-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Weekend Road Trip: Sea-to-Sky",
    "section-2": "One-Week Trip: Vancouver Island & Sunshine Coast",
    "section-3": "Extended Trip: Rainforest to Rockies",
    "section-4": "Seasonal Considerations",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.hellobc.com",
      label: "HelloBC – Official Tourism BC",
    },
    {
      url: "https://drivebc.ca",
      label: "DriveBC – Real-time Road Conditions",
    },
    {
      url: "https://www.bcferries.com",
      label: "BC Ferries – Schedules & Reservations",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1-shannon-falls",
      lat: 49.67016,
      lng: -123.15665,
      popupText: "Shannon Falls",
    },
    {
      id: "section-1-whistler",
      lat: 50.11639,
      lng: -122.95417,
      popupText: "Whistler Village",
    },
    {
      id: "section-1-joffre",
      lat: 50.34130,
      lng: -122.47620,
      popupText: "Joffre Lakes",
    },
    {
      id: "section-2-victoria",
      lat: 48.42833,
      lng: -123.36472,
      popupText: "Victoria",
    },
    {
      id: "section-2-tofino",
      lat: 49.15306,
      lng: -125.90444,
      popupText: "Tofino",
    },
    {
      id: "section-3-hells-gate",
      lat: 49.78056,
      lng: -121.44972,
      popupText: "Hell’s Gate, Fraser Canyon",
    },
    {
      id: "section-3-jasper",
      lat: 52.87306,
      lng: -118.08222,
      popupText: "Jasper Town",
    },
    {
      id: "section-3-emerald-lake",
      lat: 51.44400,
      lng: -116.53100,
      popupText: "Emerald Lake, Yoho NP",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Road Trip from Vancouver - Your Comprehensive Guide</title>
        <meta
          name="description"
          content="A detailed guide on the best road trip itineraries from Vancouver, with locations, coordinates, tips, local regulations, FAQs, and more."
        />
        <meta
          name="keywords"
          content="vancouver road trip, sea-to-sky, vancouver island, sunshine coast, bc highways"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-road-trip-from-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Road Trip from Vancouver - Your Comprehensive Guide"
        />
        <meta
          property="og:description"
          content="Discover top itineraries, hidden gems, and practical tips for a perfect road trip from Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-road-trip-from-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-road-trip-from-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Road Trip from Vancouver - Your Comprehensive Guide"
        />
        <meta
          name="twitter:description"
          content="Detailed itineraries and tips for your Vancouver road trip adventure."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-road-trip-from-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Views of Vancouver's mountainous coastline"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* INTRODUCTION */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is the perfect launch pad for unforgettable road trips,
              offering dramatic coastal highways, towering mountains, and
              charming small towns. With well-maintained roads and diverse
              landscapes, you can experience ocean vistas, lush rainforests,
              and even the Canadian Rockies in one trip. This guide will help
              you plan the best road trip from Vancouver, whether you have a
              weekend, a full week, or 10+ days. We’ll also cover local
              regulations, FAQs, and valuable resources to make your journey
              smooth and memorable.
            </p>

            {/* SECTION 1: Weekend Road Trip */}
            <h2 id="section-1">Weekend Road Trip: Sea-to-Sky Adventure</h2>
            <p>
              For a short getaway (2–3 days), the Sea-to-Sky Highway (Highway 99)
              from Vancouver to Whistler is a classic route. This mountainous
              drive winds along Howe Sound with stunning ocean views before
              climbing into the Coast Mountains. It’s perfect for travelers
              seeking a blend of easy sightseeing and outdoor adventures.
            </p>
            <p>
              <strong>Shannon Falls</strong>{" "}
              <span
                ref={(el) => setSpotRef("section-1-shannon-falls", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.67016}
                  coordX={-123.15665}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>{" "}
              is an unmissable stop just south of Squamish. With a 335 m
              waterfall easily accessible by a short trail, it’s a perfect spot
              to stretch your legs.
            </p>
            <p>
              Continue north to Squamish, where you can ride the Sea-to-Sky
              Gondola for a bird’s-eye view of Howe Sound, or explore the
              Stawamus Chief hiking trail for panoramic vistas. In the afternoon,
              head to{" "}
              <strong>Whistler Village</strong>{" "}
              <span
                ref={(el) => setSpotRef("section-1-whistler", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={50.11639}
                  coordX={-122.95417}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              , famous for its pedestrian-only village, world-class skiing, and
              vibrant dining scene. If time permits on day two, venture an hour
              further north to{" "}
              <strong>Joffre Lakes Provincial Park</strong>{" "}
              <span
                ref={(el) => setSpotRef("section-1-joffre", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={50.34130}
                  coordX={-122.47620}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>
              , where a moderate hike leads you to three turquoise mountain
              lakes—an Instagram-worthy spot for sure.
            </p>

            {/* SECTION 2: One-Week Trip */}
            <h2 id="section-2">One-Week Trip: Vancouver Island & Sunshine Coast</h2>
            <p>
              If you have 5–7 days, a circle route that includes Vancouver
              Island and the Sunshine Coast offers scenic ferries, coastal
              towns, beaches, and rainforest hikes. Start by ferrying from
              Tsawwassen to{" "}
              <strong>Victoria</strong>{" "}
              <span
                ref={(el) => setSpotRef("section-2-victoria", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={48.42833}
                  coordX={-123.36472}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>{" "}
              on Vancouver Island. Explore the historic Inner Harbour, the Royal
              BC Museum, and enjoy local seafood.
            </p>
            <p>
              Drive north to Parksville for sandy beaches and then ferry from
              Comox to Powell River on the Sunshine Coast. This region is known
              for artsy seaside communities and hidden coves perfect for
              kayaking. If you have extra time or prefer sticking to Vancouver
              Island, head to{" "}
              <strong>Tofino</strong>{" "}
              <span
                ref={(el) => setSpotRef("section-2-tofino", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.15306}
                  coordX={-125.90444}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>{" "}
              on the Pacific Rim for surfing and wild beaches. Alternatively,
              the Sunshine Coast portion includes short scenic ferry rides,
              forested hikes at Skookumchuck Narrows, and quaint towns like
              Sechelt and Gibsons. Conclude your loop by ferrying back to Horseshoe
              Bay near West Vancouver.
            </p>

            {/* SECTION 3: Extended Trip (10+ Days) */}
            <h2 id="section-3">Extended Trip: Rainforest to Rockies</h2>
            <p>
              With 10 days or more, journey eastward to the Canadian Rockies for
              the ultimate BC road adventure. Take Highway 1 through the Fraser
              Canyon, stopping at{" "}
              <strong>Hell’s Gate</strong>{" "}
              <span
                ref={(el) => setSpotRef("section-3-hells-gate", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={49.78056}
                  coordX={-121.44972}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>{" "}
              to ride the airtram over turbulent rapids. Drive onward to Kamloops
              or detour via Cache Creek to see Gold Rush-era sites.
            </p>
            <p>
              Continuing north toward Jasper brings you into dramatic mountain
              landscapes. Explore{" "}
              <strong>Jasper Town</strong>{" "}
              <span
                ref={(el) => setSpotRef("section-3-jasper", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={52.87306}
                  coordX={-118.08222}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>{" "}
              (and possibly Maligne Lake) before driving the Icefields Parkway
              (Highway 93) to Banff/Lake Louise, stopping to admire Athabasca
              Glacier and Peyto Lake. On your return, drive through Yoho National
              Park to see{" "}
              <strong>Emerald Lake</strong>{" "}
              <span
                ref={(el) => setSpotRef("section-3-emerald-lake", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <Marker
                  coordY={51.44400}
                  coordX={-116.53100}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
              </span>{" "}
              or Takakkaw Falls. You might also swing through the Okanagan
              Valley for vineyards and lake views before returning to Vancouver.
            </p>

            {/* SECTION 4: Seasonal Considerations */}
            <h2 id="section-4">Seasonal Considerations</h2>
            <p>
              <strong>Spring (March–May):</strong> Fewer crowds, but possible
              rain on the coast and lingering snow in the mountains. Perfect for
              waterfalls, wildflowers, and wildlife sightings.
            </p>
            <p>
              <strong>Summer (June–August):</strong> Peak season with warm,
              mostly dry weather. Great for hiking, camping, and enjoying long
              daylight hours. Book accommodations and ferries early.
            </p>
            <p>
              <strong>Fall (September–October):</strong> Beautiful foliage,
              cooler temps, fewer crowds. Ideal for photography, harvest season
              in the Okanagan, and decent driving conditions until late fall
              snows.
            </p>
            <p>
              <strong>Winter (November–February):</strong> Coastal areas remain
              rainy but mild; interior routes can have heavy snow. Great for
              skiing at Whistler or the Rockies. If traveling in winter, use
              proper winter tires and check road conditions frequently.
            </p>

            {/* LOCAL REGULATIONS & BEST PRACTICES */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              <ul>
                <li>
                  <strong>Driver’s License:</strong> Visitors can drive in BC
                  with a valid license from home for up to 6 months. International
                  Driving Permits are recommended if your license isn’t in
                  English or French.
                </li>
                <li>
                  <strong>Seat Belts & Speed Limits:</strong> Mandatory seat
                  belt usage; speed is posted in km/h. Typical limits are 50
                  km/h in cities, 80–100 km/h on highways (some sections allow
                  110–120 km/h).
                </li>
                <li>
                  <strong>Winter Tires:</strong> Required on many BC highways
                  Oct 1–Apr 30. Look for road signs indicating tire requirements.
                </li>
                <li>
                  <strong>No Phone Use While Driving:</strong> Handheld device
                  usage is prohibited; use hands-free if necessary.
                </li>
                <li>
                  <strong>Wildlife Safety:</strong> Don’t feed or approach
                  wildlife. Observe posted warnings. At night, watch for animals
                  on roads.
                </li>
                <li>
                  <strong>Ferries & Reservations:</strong> If traveling to
                  Vancouver Island or the Sunshine Coast in summer, booking ferry
                  reservations is recommended.
                </li>
              </ul>
            </p>
            <p>
              Always consult official websites and DriveBC for up-to-date
              road conditions, closures, and weather advisories. Respect local
              communities, carry out all trash, and practice Leave No Trace
              principles where possible.
            </p>

            {/* FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need winter tires if visiting in early spring?</h3>
              <p>
                Winter tire requirements typically run until the end of March or
                April 30 on many routes. Check specific highways and consider
                the forecast if traveling in mountainous areas.
              </p>

              <h3>2. Is it possible to drive from Vancouver to the Rockies in winter?</h3>
              <p>
                Yes, but only if you're experienced with winter conditions and
                have the correct tires. Snowstorms and ice can make the roads
                challenging, so always check road advisories before traveling.
              </p>

              <h3>3. What’s the best time of year for a Vancouver road trip?</h3>
              <p>
                Summer (June–August) offers the most reliable weather and fully
                open attractions, but September can be a sweet spot with fewer
                crowds. Each season has its unique charm, so choose based on
                your interests and comfort with potential weather variations.
              </p>
            </div>

            {/* CONCLUSION */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              A Vancouver road trip can be as short or as long as you wish.
              Whether you head north along the Sea-to-Sky for a weekend escape,
              hop between ferries for a one-week coastal loop, or conquer
              the Rainforest-to-Rockies expedition over 10+ days, British
              Columbia’s scenic roads promise unforgettable experiences.
              Plan according to your interests and the season, follow local
              regulations, and let the journey captivate you. Safe travels!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}